import {useMemo, useCallback, MouseEvent, useEffect, forwardRef} from 'react';
import {dispatchAppEvent} from '@esgi/events';
import {join} from '@esgi/ui-kit/utils';
import {Tab} from './types';
import {TabSelectedEvent} from './events';
import styles from './styles.module.less';

interface Props {
	items: Tab[];
	active?: string;
	onSelect?: (event: Record<string, any>) => void;
	className?: string;
}

export const Tabs = forwardRef<HTMLDivElement, Props>((props, ref) => {
	const {active, items, onSelect} = props;
	const activeTab = useMemo(() => {
		if (active) {
			const exist = items.find(({name}) => name === active);
			if (exist) {
				return exist;
			}
		}
		return items ? items[0] : {} as Tab;
	}, [active, items]);

	const onClick = useCallback(
		(originEvent: MouseEvent<HTMLDivElement>, tab: Tab) =>
			onSelect({tab, originEvent}),
		[],
	);

	useEffect(() => {
		setTimeout(
			() => dispatchAppEvent(TabSelectedEvent, new TabSelectedEvent(activeTab)),
			100,
		);
	}, [activeTab]);

	if (!items) {
		return;
	}

	return (
		<div ref={ref} className={join(styles.tabs, props.className)}>
			{items.map((tab) => {
				const isActiveTab = activeTab.name === tab.name;

				return (
					<div
						key={tab.name}
						className={join(styles.tab, isActiveTab && styles.tabActive)}
						onClick={(event) => onClick(event, tab)}
						data-tab=''
						data-tab-active={isActiveTab}
					>
						{tab.label}
					</div>
				);
			})}
		</div>
	);
});
